/* ==========================================================================
   Dry Eye Rescue - PDP description accordions (Avenova test)
   Loaded only by sections/der-pdp-top.liquid, which only exists in
   templates/product.der-pdp-test.json.

   What it does, and what it deliberately does not do:
   - The CIN7-synced product description is printed normally by Liquid, so it
     is in the HTML source for crawlers before this file runs. Nothing is
     fetched, lazy-loaded or duplicated.
   - It builds the accordion from a DETACHED CLONE first. The visible
     description is only replaced once the whole accordion has been built
     without throwing, so a parse failure leaves the full description on
     screen instead of a blank panel.
   - Content is moved, never copied: after the swap the description exists
     exactly once in the DOM.
   No dependencies. ~4 KB.
   ========================================================================== */
(function () {
  'use strict';

  var HEADING_TAGS = { H2: 1, H3: 1, H4: 1 };
  var uid = 0;

  function isBlank(node) {
    return node.nodeType === 3 && !/\S/.test(node.nodeValue);
  }

  function isRealHeading(node) {
    return node.nodeType === 1 && HEADING_TAGS[node.tagName] === 1;
  }

  /* A <p> counts as a heading only when the paragraph IS a single bold run,
     is short, does not end like a sentence, and is not the last node. This is
     a fallback: it is only consulted when the description has no h2/h3/h4 at
     all, so a bold sentence inside a normally structured description can
     never be mistaken for a section break. */
  function isBoldHeading(node, isLast) {
    if (node.nodeType !== 1 || node.tagName !== 'P' || isLast) return false;
    if (node.children.length !== 1) return false;
    var inner = node.children[0];
    if (inner.tagName !== 'STRONG' && inner.tagName !== 'B') return false;
    var text = (node.textContent || '').trim();
    if (text !== (inner.textContent || '').trim()) return false;
    if (text.length < 2 || text.length > 70) return false;
    if (/[.,;]$/.test(text)) return false;
    return true;
  }

  function meaningful(nodes) {
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.nodeType === 1) return true;
      if (n.nodeType === 3 && /\S/.test(n.nodeValue)) return true;
    }
    return false;
  }

  /* Split a flat list of nodes into [{heading|null, label, nodes[]}] */
  function group(nodes, test) {
    var groups = [];
    var current = null;
    var intro = [];
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (test(node, i === nodes.length - 1)) {
        current = { heading: node, label: (node.textContent || '').trim(), nodes: [] };
        groups.push(current);
      } else if (current) {
        current.nodes.push(node);
      } else {
        intro.push(node);
      }
    }
    return { intro: intro, groups: groups };
  }

  function buildItem(headingEl, labelText, bodyNodes, open, tagFallback) {
    uid += 1;
    var btnId = 'der-acc-btn-' + uid;
    var panelId = 'der-acc-panel-' + uid;

    var item = document.createElement('div');
    item.className = 'der-acc__item';

    var head = headingEl;
    if (!head) {
      head = document.createElement(tagFallback || 'h3');
    }
    head.className = 'der-acc__head';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'der-acc__btn';
    btn.id = btnId;
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    btn.setAttribute('aria-controls', panelId);

    var label = document.createElement('span');
    label.className = 'der-acc__label';
    if (headingEl) {
      while (head.firstChild) label.appendChild(head.firstChild);
    } else {
      label.appendChild(document.createTextNode(labelText));
    }
    btn.appendChild(label);

    var icon = document.createElement('span');
    icon.className = 'der-acc__icon';
    icon.setAttribute('aria-hidden', 'true');
    btn.appendChild(icon);

    head.appendChild(btn);

    var panel = document.createElement('div');
    panel.className = 'der-acc__panel';
    panel.id = panelId;
    panel.setAttribute('role', 'region');
    panel.setAttribute('aria-labelledby', btnId);
    if (!open) panel.hidden = true;

    var body = document.createElement('div');
    body.className = 'der-acc__body rte';
    for (var i = 0; i < bodyNodes.length; i++) body.appendChild(bodyNodes[i]);
    panel.appendChild(body);

    item.appendChild(head);
    item.appendChild(panel);
    return item;
  }

  function build(src, opts) {
    /* Work on a clone so the live description is untouched until we succeed. */
    var work = src.cloneNode(true);
    var nodes = [];
    for (var i = 0; i < work.childNodes.length; i++) {
      if (!isBlank(work.childNodes[i])) nodes.push(work.childNodes[i]);
    }
    if (!nodes.length) return null;

    var res = group(nodes, isRealHeading);
    if (!res.groups.length) res = group(nodes, isBoldHeading);

    var acc = document.createElement('div');
    acc.className = 'der-acc';

    if (!res.groups.length) {
      /* Mandatory fallback: one accordion holding the whole description. */
      acc.appendChild(buildItem(null, opts.fallbackLabel, nodes, true, 'h3'));
      acc.setAttribute('data-der-acc-mode', 'fallback');
    } else {
      var index = 0;
      if (meaningful(res.intro)) {
        acc.appendChild(buildItem(null, opts.overviewLabel, res.intro, true, 'h3'));
        index = 1;
      }
      for (var g = 0; g < res.groups.length; g++) {
        var grp = res.groups[g];
        if (!grp.label) continue;
        acc.appendChild(buildItem(grp.heading, grp.label, grp.nodes, index === 0, null));
        index += 1;
      }
      acc.setAttribute('data-der-acc-mode', 'parsed');
    }

    if (!acc.children.length) return null;
    return acc;
  }

  function wire(acc) {
    var btns = acc.querySelectorAll('.der-acc__btn');

    acc.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('.der-acc__btn') : null;
      if (!btn || !acc.contains(btn)) return;
      var panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (!panel) return;
      var open = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', open ? 'false' : 'true');
      panel.hidden = open;
    });

    /* Arrow / Home / End move between headers. Enter and Space are native. */
    acc.addEventListener('keydown', function (e) {
      var btn = e.target.closest ? e.target.closest('.der-acc__btn') : null;
      if (!btn) return;
      var list = Array.prototype.slice.call(btns);
      var i = list.indexOf(btn);
      if (i === -1) return;
      var next = null;
      if (e.key === 'ArrowDown') next = list[(i + 1) % list.length];
      else if (e.key === 'ArrowUp') next = list[(i - 1 + list.length) % list.length];
      else if (e.key === 'Home') next = list[0];
      else if (e.key === 'End') next = list[list.length - 1];
      if (next) { e.preventDefault(); next.focus(); }
    });
  }

  function init() {
    var wrap = document.querySelector('[data-der-pdp-acc]');
    if (!wrap) return;
    var src = wrap.querySelector('[data-der-pdp-src]');
    if (!src) return;
    if (src.getAttribute('data-der-acc-done') === '1') return;

    var acc;
    try {
      acc = build(src, {
        overviewLabel: wrap.getAttribute('data-overview-label') || 'Product Overview',
        fallbackLabel: wrap.getAttribute('data-fallback-label') || 'Product Information'
      });
    } catch (err) {
      if (window.console && console.warn) console.warn('[der-pdp] accordion skipped, showing full description:', err);
      return;
    }
    if (!acc) return;

    /* Commit: only now is the raw description replaced. */
    try {
      while (src.firstChild) src.removeChild(src.firstChild);
      src.appendChild(acc);
      src.setAttribute('data-der-acc-done', '1');
      wrap.classList.add('der-pdp-info--acc');
      wire(acc);
    } catch (err2) {
      if (window.console && console.warn) console.warn('[der-pdp] accordion commit failed:', err2);
    }
  }

  /* ----------------------------------------------------------------------
     Variant chip.

     The chip is printed by Liquid from the variant selected at page load.
     Switching variants on the page is client side - the theme swaps the price
     and the hidden id without a reload - so without this the chip would keep
     showing the variant the shopper arrived on. It reads the checked inputs
     rather than any theme-specific event payload, so it keeps working if the
     theme changes how it announces variant changes.
     ---------------------------------------------------------------------- */
  function variantLabel() {
    var scope = document.querySelector('.product-options') ||
                document.querySelector('variant-picker');
    if (!scope) return '';
    var parts = [];
    var checked = scope.querySelectorAll('input[type="radio"]:checked');
    if (checked.length) {
      for (var i = 0; i < checked.length; i++) parts.push(checked[i].value);
    } else {
      var sels = scope.querySelectorAll('select');
      for (var j = 0; j < sels.length; j++) if (sels[j].value) parts.push(sels[j].value);
    }
    return parts.join(' / ').replace(/\s+/g, ' ').trim();
  }

  function syncVariantChip() {
    var chip = document.querySelector('[data-der-variant-chip]');
    if (!chip) return;
    var label = variantLabel();
    if (!label || /^default title$/i.test(label)) {
      chip.hidden = true;
      return;
    }
    chip.hidden = false;
    chip.textContent = label;
  }

  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (t.closest('.product-options') || t.closest('variant-picker')) {
      /* after the theme has finished updating the form */
      setTimeout(function () {
        syncVariantChip();
        syncFactChips();
        dedupeChips();
      }, 0);
    }
  }, true);

  /* ----------------------------------------------------------------------
     The breadcrumb and the chip row are rendered by different sections, so
     neither can see what the other printed. On products where the breadcrumb
     falls back to the Cin7 sub category, the same words appear twice on one
     screen - "AREDS & Macular Support" as the crumb and again as a chip.

     Comparing the rendered text is exact and needs no duplicated Liquid, so the
     chip is dropped when it repeats the breadcrumb category. Chips that add
     something the crumb does not (Avenova: crumb "Eyelid Cleansers", chip
     "Hypochlorous Acid Sprays") are left alone.
     ---------------------------------------------------------------------- */
  function dedupeChips() {
    var crumbs = document.querySelectorAll('.der-pdp-crumbs li');
    if (crumbs.length < 3) return;               /* Home + category + product */
    var category = (crumbs[1].textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
    if (!category) return;
    var chips = document.querySelectorAll('.der-pdp-chips li');
    for (var i = 0; i < chips.length; i++) {
      if (chips[i].hasAttribute('data-der-variant-chip')) continue;
      var t = (chips[i].textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (t === category) chips[i].hidden = true;
    }
    var list = document.querySelector('.der-pdp-chips');
    if (list && !list.querySelector('li:not([hidden])')) list.hidden = true;
  }

  /* ----------------------------------------------------------------------
     Size and supply chips.

     Both are read from the selected VARIANT's metafields, so each bottle of a
     multi-size product carries its own values. Liquid renders the variant the
     shopper lands on; switching variants happens without a reload, so the
     values for every variant are printed once as JSON and swapped in here.
     ---------------------------------------------------------------------- */
  /* The data arrives as id|size|supply records separated by ~, not JSON:
     Liquid cannot emit a literal closing brace next to its own delimiter, and
     Shopify rejects the file if you try. */
  var factsCache;
  function variantFacts() {
    if (factsCache !== undefined) return factsCache;
    var el = document.querySelector('[data-der-variant-facts]');
    if (!el) { factsCache = null; return factsCache; }
    var map = {};
    var records = (el.textContent || '').split('~');
    for (var i = 0; i < records.length; i++) {
      var parts = records[i].split('|');
      if (parts.length < 3) continue;
      var id = parts[0].trim();
      if (id) map[id] = { size: parts[1].trim(), supply: parts[2].trim() };
    }
    factsCache = map;
    return factsCache;
  }

  function setChip(selector, value) {
    var el = document.querySelector(selector);
    if (!el) return;
    if (!value) { el.hidden = true; return; }
    el.textContent = value;
    el.hidden = false;
  }

  function syncFactChips() {
    var map = variantFacts();
    if (!map) return;
    var input = document.querySelector('input[name="id"]');
    var f = input && map[input.value];
    if (!f) return;
    setChip('[data-der-size-chip]', f.size);
    setChip('[data-der-supply-chip]', f.supply);
  }

  function boot() {
    init();
    syncVariantChip();
    syncFactChips();
    dedupeChips();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  /* Rebuilt if the section is re-rendered in the theme editor. */
  document.addEventListener('shopify:section:load', boot);
})();
